import {
  ResetPasswordPage,
  type ResetPasswordPageMessages,
  useConfirmPasswordResetMutation,
  useSendPasswordResetCodeMutation,
} from "@assetwatch/auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

const resetPasswordSearchSchema = z.object({
  code: z.coerce.string().optional(),
  email: z.string().optional(),
  expired: z.union([z.enum(["true", "false"]), z.boolean()]).optional(),
});

type ResetPasswordSearch = z.infer<typeof resetPasswordSearchSchema>;

const messages: ResetPasswordPageMessages = {
  assetWatchLabel: "AssetWatch",
  backToSignIn: "Back to login",
  confirmNewPasswordLabel: "Confirm password",
  continueToSignIn: "Back to login",
  description: "Request a reset code, then choose a new password.",
  emailLabel: "Email",
  emailPlaceholder: "Email",
  emailSentMessage: "Reset code sent. Check your email.",
  emailSentTitle: "Reset code sent",
  expiredLinkDescription:
    "This reset link has expired. Send a new code to continue.",
  expiredLinkTitle: "Reset link expired",
  forgotPasswordDescription:
    "Request a reset code, then choose a new password.",
  forgotPasswordTitle: "Reset password",
  hidePassword: "Hide password",
  loadingSendingCode: "Sending code",
  loadingUpdatingPassword: "Updating password",
  newPasswordLabel: "New password",
  passwordRequirementsIntro: "Your new password must:",
  passwordSavedMessage: "Your password has been updated.",
  passwordSavedTitle: "Password updated",
  passwordsDoNotMatch: "Passwords do not match.",
  reachOutToSupport: "Reach out to support",
  resetCodeLabel: "Reset code",
  saveChanges: "Update password",
  sendNewLink: "Send new code",
  sendResetCode: "Send reset code",
  showPassword: "Show password",
  supportQuestion: "Questions?",
  title: "Reset password",
  errors: {
    codeMismatch: "The reset code is incorrect.",
    expiredCode:
      "The reset code has expired. Request a new code and try again.",
    generic: "Unable to reset your password. Try again.",
    invalidPassword: "The new password does not meet the requirements.",
    limitExceeded: "Too many reset attempts. Wait a few minutes and try again.",
    passwordHistoryViolation: "Choose a password you have not used before.",
    unverifiedContact: "No verified email is registered.",
    userNotFound: "No account was found for that email address.",
  },
};

export const Route = createFileRoute("/login/reset")({
  validateSearch: (search): ResetPasswordSearch =>
    resetPasswordSearchSchema.parse(search),
  component: ResetPasswordRoute,
});
function isExpiredSearchValue(expired: ResetPasswordSearch["expired"]) {
  return expired === true || expired === "true";
}
function ResetPasswordRoute() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const confirmPasswordResetMutation = useConfirmPasswordResetMutation();
  const sendPasswordResetCodeMutation = useSendPasswordResetCodeMutation();
  return (
    <ResetPasswordPage
      confirmPasswordReset={(variables) =>
        confirmPasswordResetMutation.mutateAsync(variables)
      }
      initialCode={search.code}
      initialEmail={search.email}
      initialExpired={isExpiredSearchValue(search.expired)}
      isConfirming={confirmPasswordResetMutation.isPending}
      isSendingCode={sendPasswordResetCodeMutation.isPending}
      messages={messages}
      onNavigateToSignIn={() => void navigate({ to: "/" })}
      sendPasswordResetCode={(username) =>
        sendPasswordResetCodeMutation.mutateAsync({ username })
      }
    />
  );
}
